// components/ChatThread/imageCompression.js
// ============================================================
// Compresse une image côté navigateur avant l'envoi au serveur.
// Cloudinary refuse tout fichier de plus de 10 Mo — cet utilitaire
// redimensionne et recompresse l'image pour rester largement en
// dessous de cette limite, tout en gardant une bonne qualité visuelle.
// ============================================================

const MAX_DIMENSION = 1600   // px sur le plus grand côté — largement suffisant pour un chat
const TARGET_MAX_MB = 3      // taille cible après compression
const MIN_QUALITY   = 0.35   // qualité JPEG minimale acceptée (évite un résultat trop dégradé)

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file)
  const img = new Image()
  img.onload  = () => { resolve(img); URL.revokeObjectURL(url) }
  img.onerror = (err) => { URL.revokeObjectURL(url); reject(err) }
  img.src = url
})

const canvasToBlob = (canvas, quality) => new Promise((resolve) => {
  canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality)
})

/**
 * Compresse un File image et retourne un nouveau File (JPEG), redimensionné
 * et recompressé si nécessaire. Si le fichier est déjà petit, ou si la
 * compression échoue pour une raison quelconque (image corrompue, navigateur
 * trop ancien…), le fichier original est renvoyé tel quel — le serveur
 * appliquera de toute façon sa propre validation.
 */
export async function compressImage(file, {
  maxDimension = MAX_DIMENSION,
  targetMaxMB  = TARGET_MAX_MB,
} = {}) {
  // Fichier déjà suffisamment léger → inutile de le retoucher
  if (file.size <= targetMaxMB * 1024 * 1024) {
    return file
  }

  try {
    const img = await loadImage(file)
    let { width, height } = img
    const scale = Math.min(1, maxDimension / Math.max(width, height))
    width  = Math.max(1, Math.round(width  * scale))
    height = Math.max(1, Math.round(height * scale))

    const canvas = document.createElement('canvas')
    canvas.width  = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0, width, height)

    let quality = 0.85
    let blob    = await canvasToBlob(canvas, quality)

    // Redescend progressivement la qualité si le résultat est encore trop lourd
    while (blob && blob.size > targetMaxMB * 1024 * 1024 && quality > MIN_QUALITY) {
      quality = Math.max(MIN_QUALITY, quality - 0.15)
      blob = await canvasToBlob(canvas, quality)
    }

    // toBlob non supporté par ce navigateur → on retente avec le fichier d'origine
    if (!blob) return file

    const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg'
    return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() })
  } catch {
    return file
  }
}

/**
 * Compresse plusieurs fichiers en parallèle.
 */
export async function compressImages(files, options) {
  return Promise.all(files.map((f) => compressImage(f, options)))
}